import React from "react";
import dayjs from "dayjs";
import ModalImage from "react-modal-image";
import { formatMoney } from "utils";

const TxnsTableView = ({
  txns = [],
  openBalance = 0,
}) => {
  const getVal = (item, key) =>
    typeof item?.get === "function" ? item.get(key) : item?.[key];

  const renderAttachment = (attachment) => {
    if (!attachment) {
      return <span className="text-muted"></span>;
    }

    const isPDF = attachment?.includes(".pdf");
    const isImage = attachment?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

    if (isPDF) {
      return (
        <a
          role="button"
          href={attachment}
          target="_blank"
          rel="noreferrer"
          className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 py-1 px-2 rounded"
          title="Open PDF Attachment"
        >
          <i className="bi bi-file-pdf-fill fs-6" />
          <span className="d-none d-lg-inline fs-xmd-smaller">PDF</span>
        </a>
      );
    }

    if (isImage) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center"
          title="Click to view attachment"
        >
          <ModalImage
            small={attachment}
            large={attachment}
            hideDownload={false}
            hideZoom={false}
            alt="Attachment"
            className="table-attachment-img rounded border shadow-sm cursor-pointer"
          />
        </div>
      );
    }

    return (
      <a
        role="button"
        href={attachment}
        target="_blank"
        rel="noreferrer"
        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 py-1 px-2 rounded"
        title="Open Attachment"
      >
        <i className="bi bi-paperclip fs-6" />
        <span className="d-none d-lg-inline fs-xmd-smaller">View</span>
      </a>
    );
  };

  // Filter out cancelled transactions for public view
  const activeTxns = txns.filter((item) => {
    const actualTxn = item?.txn || item;
    return !getVal(actualTxn, "cancelled");
  });

  return (
    <div className="w-100 p-0 m-0">
      {/* Statement Table */}
      <div
        className="table-responsive scrollbar-design w-100 p-0"
        style={{
          height: "calc(100vh - 225px)",
          maxHeight: "calc(100vh - 225px)",
          overflowY: "auto",
        }}
      >
        <table className="table table-hover align-middle mb-0 khata-statement-table">
          <thead className="table-light position-sticky top-0 z-1 border-bottom">
            <tr>
              <th
                scope="col"
                className="text-start py-3 px-3 text-secondary text-nowrap"
                style={{ width: "140px" }}
              >
                Txn Date
              </th>
              <th scope="col" className="text-start py-3 px-2 text-secondary">
                Description
              </th>
              <th
                scope="col"
                className="text-end py-3 px-3 text-secondary text-nowrap"
                style={{ width: "160px" }}
              >
                Amount Received
              </th>
              <th
                scope="col"
                className="text-end py-3 px-3 text-secondary text-nowrap"
                style={{ width: "160px" }}
              >
                Amount Given
              </th>
              <th
                scope="col"
                className="text-end py-3 px-2 text-secondary text-nowrap"
                style={{ width: "110px" }}
              >
                Balance
              </th>
            </tr>
            <tr>
              <th colSpan={2} className="text-start fw-bold text-secondary px-3">Opening Balance</th>
              <th></th>
              <th></th>
              <th className={`fw-bold fs-xmd-smaller text-end`}>{formatMoney(openBalance)}</th>
            </tr>
          </thead>
          <tbody>
            {activeTxns.length > 0 && (
              activeTxns.map((item, index) => {
                const actualTxn = item?.txn || item;
                const balance =
                  item?.balance !== undefined
                    ? item.balance
                    : actualTxn?.balance ?? 0;
                const txnId = actualTxn?.id || item?.id;
                const amount = getVal(actualTxn, "amount") || 0;
                const detail = getVal(actualTxn, "detail") || "-";
                const date = getVal(actualTxn, "date");
                const attachment = getVal(actualTxn, "attachment");
                const type = getVal(actualTxn, "type");
                const isReceived = amount > 0;
                const isGiven = amount < 0;

                return (
                  <tr key={txnId || index} className="border-bottom">
                    {/* Txn Date */}
                    <td className="text-start py-2 px-3 text-nowrap">
                      <span className="fs-xmd-smaller text-dark">
                        {dayjs(date).format("DD MMM YYYY")}
                      </span>
                    </td>

                    {/* Description */}
                    <td className="text-start py-2 px-2">
                      <div className="d-flex align-items-center flex-wrap">
                        <span className="text-break text-dark me-2">{detail}</span>
                        {renderAttachment(attachment)}
                        {type === "CREDIT" && (
                          <span
                            className="badge border border-danger text-danger ms-2"
                            style={{ fontSize: "10px" }}
                          >
                            CREDIT
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Amount Received */}
                    <td className="text-end py-2 px-3 text-nowrap">
                      {isReceived ? (
                        <span className="fw-semibold text-muted">
                          {formatMoney(amount)}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    {/* Amount Given */}
                    <td className="text-end py-2 px-3 text-nowrap">
                      {isGiven ? (
                        <span className="fw-semibold text-muted">
                          {formatMoney(Math.abs(amount))}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    {/* Balance */}
                    <td className="text-end py-2 px-2 text-nowrap">
                      <span className="fw-semibold text-muted">
                        {formatMoney(balance)}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TxnsTableView;
